<template>
    <v-card height="100%">
        <v-card-title :class="getTitleClass(cardBack)">
            <v-row no-gutters>
                <v-col>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-icon v-on="on" class="mr-2" dense dark>
                                {{ cardBack.cardFront.cardType.icon }}
                            </v-icon>
                        </template>
                        <span>{{ cardBack.cardFront.cardType.title }}</span>
                    </v-tooltip>
                    <span class="subtitle-2 white--text"
                          v-if="cardBack.cardFront.price">{{ cardBack.cardFront.price }} - </span>
                    <span class="subtitle-2 white--text">{{ title }}</span>
                </v-col>
            </v-row>
        </v-card-title>

        <v-card-text class="pb-0">
            <v-row no-gutters class="mt-4">
                <v-row no-gutters>
                    <v-col>
                        <span v-for="(cardTrait, cardTraitIndex) in cardBack.cardFront.cardTraits"
                              :key="'cardTrait' + cardTraitIndex"
                              class="caption font-weight-bold">
                            <span v-if="cardTraitIndex > 0"> - </span>{{ cardTrait.title }}
                        </span>
                    </v-col>
                </v-row>
            </v-row>

            <v-row style="margin-bottom: 30px;">
                <v-col>
                    <div v-if="cardBack.cardFront.turned_over">
                        <p v-for="(description, descriptionIndex) in cardBack.description"
                           :key="'description' + descriptionIndex"
                           v-html="iconifyText(description)"
                           class="body-2">
                            {{ iconifyText(description) }}
                        </p>
                    </div>
                    <div v-else>
                        <p v-for="(cardAttribute, cardAttributeIndex) in cardBack.cardFront.cardAttributes"
                           :key="'cardAttribute' + cardAttributeIndex"
                           class="body-2">
                            <strong v-if="cardAttribute.cardAttributeTypes.find(type => type.title === 'Reckoning')">
                                Reckoning:
                            </strong>
                            <strong v-else-if="cardAttribute.cardAttributeTypes.find(type => type.title === 'Action')">
                                Action:
                            </strong>
                            <strong v-else-if="cardAttribute.cardAttributeTypes.find(type => type.title === 'Local Action')">
                                Local Action:
                            </strong>
                            <span v-html="iconifyText(cardAttribute.description)">{{ iconifyText(cardAttribute.description) }}</span>
                        </p>
                    </div>
                </v-col>
            </v-row>

            <v-row style="position: absolute; bottom: 30px; height: 30px;">
                <v-col>
                    <v-menu offset-y>
                        <template #activator="{ on: onMenu }">
                            <v-tooltip bottom>
                                <template #activator="{ on: onTooltip }">
                                    <v-btn icon
                                           color="primary"
                                           v-on="{ ...onMenu, ...onTooltip }">
                                        <v-icon>mdi-account-arrow-right</v-icon>
                                    </v-btn>
                                </template>

                                <span>Move to</span>
                            </v-tooltip>
                        </template>

                        <v-list>
                            <v-list-item
                                    v-for="(investigator, investigatorIndex) in game.activeInvestigatorSessions"
                                    :key="investigatorIndex"
                                    @click="giveTo(investigatorIndex)"
                            >
                                <v-list-item-title>{{ investigator.investigator.title }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="discarded" @click="moveToDeck">
                                <v-list-item-title>Deck</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>

                    <v-tooltip bottom>
                        <template #activator="{on}">
                            <v-btn icon
                                   @click="turnOver"
                                   v-on="on"
                                   v-if="cardBack.title !== null || cardBack.description"
                                   color="primary">
                                <v-icon>mdi-sync</v-icon>
                            </v-btn>
                        </template>

                        <span>Turn over</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                        <template #activator="{on}">
                            <v-btn icon
                                   @click="detachCard"
                                   v-if="discarded !== true"
                                   v-on="on"
                                   color="error">
                                <v-icon>mdi-close-circle</v-icon>
                            </v-btn>
                        </template>

                        <span>Discard</span>
                    </v-tooltip>
                </v-col>
            </v-row>

            <span v-if="cardBack.cardFront.reckoning" class="reckoning red--text">O</span>
        </v-card-text>
    </v-card>
</template>

<script>
  import InvestigatorSessionService from "../services/InvestigatorSessionService";
  import GameService from "../services/GameService";
  import CardBackService from "../services/CardBackService";

  export default {
    props: ["cardBack", "investigator", "reserve", "discarded", "game"],

    computed: {
      title: function () {
        if (this.cardBack.cardFront.turned_over && this.cardBack.title) {
          return this.cardBack.title;
        }

        return this.cardBack.cardFront.title;
      }
    },

    methods: {
      detachCard() {
        if (confirm(`Are you sure you want to discard the ${this.cardBack.cardFront.title}?`)) {
          if (this.cardBack.cardFront.cardType.title === "Asset" || this.cardBack.cardFront.cardType.title === "Unique Asset" || this.cardBack.cardFront.cardType.title === "Artifact") {
            this.discardCard();
          } else {
            InvestigatorSessionService.detachCard(this.investigator.id, this.cardBack.id)
              .then(() => {
                this.$emit("detachedCard");
              });
          }
        }
      },

      turnOver() {
        this.cardBack.cardFront.turned_over = !this.cardBack.cardFront.turned_over;
      },

      discardCard() {
        GameService.discardCard(this.game.id, this.cardBack.id)
          .then(() => {
            this.$emit("detachedCard");
          });
      },

      giveTo(investigatorIndex) {
        let investigator = this.game.activeInvestigatorSessions[investigatorIndex];
        if (confirm(`Are you sure you want to give ${this.cardBack.cardFront.title} to ${investigator.title}?`)) {
          CardBackService.attachToInvestigatorSession(this.cardBack.id, investigator.id)
            .then(() => {
              this.$emit("detachedCard");
              this.$emit("givenTo", investigatorIndex);
            });
        }
      },

      moveToDeck() {
        GameService.detachCard(this.game.id, this.cardBack.id)
          .then(() => {
            this.$emit("detachedCard");
          });
      },

      iconifyText(text) {
        text = this.iconifyString(text, ', test Lore', ' A');
        text = this.iconifyString(text, 'Lore', 'A');

        text = this.iconifyString(text, ', test Influence', ' S');
        text = this.iconifyString(text, 'Influence', 'S');

        text = this.iconifyString(text, ', test Observation', ' D');
        text = this.iconifyString(text, 'Observation', 'D');

        text = this.iconifyString(text, ', test Strength', ' F');
        text = this.iconifyString(text, 'Strength', 'F');

        text = this.iconifyString(text, ', test Will', ' G');
        text = this.iconifyString(text, 'Will', 'G');

        return text;
      },

      iconifyString(text, needle, replace) {
        return text.replace(needle, '<span class="eh">' + replace + '</span>');
      },

      getTitleClass(cardBack) {
        switch (cardBack.cardFront.cardType.title) {
          case 'Asset':
            return "orange darken-3";
          case 'Spell':
            return "purple lighten-1";
          case 'Unique Asset':
            return "orange lighten-1";
          case 'Condition':
            return "grey darken-1";
          case 'Artifact':
            return "red darken-4";
        }
      }
    }
  };
</script>

<style>
    @font-face {
        font-family: eldritch;
        src: url(../../src/assets/EldritchHorrorIcons_v2_1.ttf) format("truetype");
    }

    .eh {
        font-family: eldritch, serif;
        font-size: 1.2em;
    }

    .reckoning {
        position: absolute;
        bottom: 20px;
        right: 20px;
        font-size: 2.2em;
        font-family: eldritch, serif;
    }
</style>